import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    AssortmentGetProductsPOSTRequest,
    CatalogNodeModel,
    FilterCriteriaType,
    FilterGroup,
    FilterGroupType,
    FilterOutput,
    FilterSearch,
    ShopAssortmentType,
    ShopListProduct,
    ShopListProductPageInfo,
} from 'microshop-api';
import services from 'services';
import { AppDispatch, RootState } from 'store';
import { KeyValueString } from 'types/KeyValuePair';

let getProductsController: AbortController | null;
let getFiltersController: AbortController | null;

export function getFilterParam(filters: ActiveFilter[]) {
    let result: FilterGroup[] = [];

    if (filters.length) {
        const userFilters = filters.reduce<FilterSearch[]>((acc, filter) => {
            if (filter.values.length) {
                return [
                    ...acc,
                    {
                        n: filter.type,
                        v: filter.values.map((f) => f.key),
                        e: false,
                    },
                ];
            }

            return acc;
        }, []);
        result.push({
            t: FilterGroupType.And,
            c: FilterCriteriaType.Include,
            f: userFilters,
        });
    }

    return JSON.stringify(result);
}

export const All_FilterKind = ['color', 'brand', 'gender', 'certification'];

export type FilterKindTuples = typeof All_FilterKind;
export type FilterKind = FilterKindTuples[number];
export type OrderBy = 'relevance' | 'number' | 'name' | 'new';

export type ActiveFilter = {
    type: FilterKind;
    values: KeyValueString[];
};

export type FilterToggleAction = {
    type: FilterKind;
    value: KeyValueString;
};

type SortName = 'relevance' | 'new' | 'name' | 'number' | 'priceDesc';
export type SortOption = {
    sort: SortName;
    visible: boolean;
};

export type AssortmentPageInfo = {
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    totalItems?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
};

type AssortmentState = {
    products: ShopListProduct[];
    loading: boolean;
    showBrand: boolean;
    currentPathName: string;

    categories: CategoryUrlMaps;
    categoriesLoading: boolean;
    categoriesRaw: CatalogNodeModel[];
    assortmentPageInfo: AssortmentPageInfo;

    filters: FilterOutput[];
    filtersActive: ActiveFilter[];
    searchPhrase?: string;
    filtersLoading: boolean;
    filtersShow: boolean;

    selectedOrderBy?: OrderBy;

    searchResults: {
        results: ShopListProduct[];
        loading: boolean;
        totalItems?: number;
    };
};

const initialState: AssortmentState = {
    products: [],
    loading: false,
    showBrand: true,
    currentPathName: '',

    assortmentPageInfo: {
        currentPage: 1,
    },

    categories: {},
    categoriesRaw: [],
    categoriesLoading: false,

    filters: [],
    filtersActive: [],
    filtersLoading: false,
    filtersShow: true,

    searchResults: {
        results: [],
        loading: false,
    },
};

const assortmentSlice = createSlice({
    name: 'assortment',
    initialState,
    reducers: {
        filterToggled(state, action: PayloadAction<FilterToggleAction>) {
            const { type, value } = action.payload;
            const filter = state.filtersActive.find((f) => f.type === type);

            if (!filter) {
                state.filtersActive = [...state.filtersActive, { type, values: [value] }];
            } else if (filter?.values.find((v) => v.key === value.key)) {
                state.filtersActive = [
                    ...state.filtersActive.filter((f) => f.type !== type),
                    { ...filter, values: filter.values.filter((f) => f.key !== value.key) },
                ];
            } else {
                state.filtersActive = [
                    ...state.filtersActive.filter((f) => f.type !== type),
                    { ...filter, values: [...filter.values, value] },
                ];
            }
        },
        showFilterToggled(state, action: PayloadAction<boolean | undefined>) {
            state.filtersShow = action.payload !== undefined ? action.payload : !state.filtersShow;
        },
        filtersCleared(state) {
            state.filtersActive = [];
        },
        selectedOrderByChanged(state, action: PayloadAction<OrderBy>) {
            state.selectedOrderBy = action.payload;
        },
        setAssortmentLocation(state, action: PayloadAction<string>) {
            state.currentPathName = action.payload.toLowerCase();
        },
        searchPhraseSet(state, action: PayloadAction<string>) {
            state.searchPhrase = action.payload;
        },
        searchPhraseClear(state) {
            state.searchPhrase = undefined;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadProducts.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(loadProducts.fulfilled, (state, action: PayloadAction<ShopListProductPageInfo>) => {
            const {
                items,
                hasNextPage,
                currentPage,
                hasPreviousPage,
                pageSize,
                totalItems,
                totalPages,
            } = action.payload;

            state.products = items ?? [];
            state.assortmentPageInfo = {
                hasNextPage,
                currentPage,
                hasPreviousPage,
                pageSize,
                totalItems,
                totalPages,
            };
            state.loading = false;
        });
        builder.addCase(getAllCategories.pending, (state) => {
            state.categoriesLoading = true;
        });
        builder.addCase(getAllCategories.fulfilled, (state, action) => {
            state.categories = { ...action.payload.categories };
            state.categoriesRaw = [...action.payload.categoriesRaw];
            state.categoriesLoading = false;
        });
        builder.addCase(getFilters.pending, (state) => {
            state.filtersLoading = true;
        });
        builder.addCase(getFilters.fulfilled, (state, action) => {
            const filters = action.payload.reduce((acc, filter) => {
                return [...acc, filter];
            }, [] as FilterOutput[]);

            state.filters = filters;
            state.filtersLoading = false;
        });
        builder.addCase(searchProducts.pending, (state) => {
            state.searchResults.loading = true;
        });
        builder.addCase(searchProducts.fulfilled, (state, action) => {
            state.searchResults.loading = false;
            state.searchResults.results = action.payload.items ?? [];
            state.searchResults.totalItems = action.payload.totalItems;
        });
    },
});

export const loadProducts = createAsyncThunk<
    ShopListProductPageInfo,
    { page?: number; all?: boolean; featured?: boolean; search?: string } | undefined,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/load_products', async (settings, { getState }) => {
    if (getProductsController?.signal && !getProductsController?.signal.aborted) getProductsController.abort();

    const searchParams = selectSearchParams(getState());
    const isLandingPage = window.location.pathname === '/';

    getProductsController = new AbortController();

    const response = await services.assortment.getListProducts(
        {
            ...searchParams,
            orderBy: isLandingPage ? 'featured' : undefined,
            page: settings?.page ?? searchParams.page,
        },
        getProductsController.signal,
    );

    return response;
});

export const searchProducts = createAsyncThunk<
    ShopListProductPageInfo,
    { search: string },
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/searchProducts', async (settings, { getState }) => {
    const response = await services.assortment.getListProducts({
        search: settings?.search,
        requestBody: selectSearchParams(getState()).requestBody,
        orderBy: 'relevance',
        page: 1,
        pageSize: 5,
    });
    return response;
});

export const loadAssortmentLocation = createAsyncThunk<
    void,
    string,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/set_assortment_location', async (url, { dispatch }) => {
    dispatch(setAssortmentLocation(url));
});

export const changeOrderBy = createAsyncThunk<
    void,
    OrderBy,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/changeOrderBy', async (orderBy, { dispatch }) => {
    dispatch(assortmentSlice.actions.selectedOrderByChanged(orderBy));

    dispatch(loadProducts({ page: 1 }));
});

export const getFilters = createAsyncThunk<
    FilterOutput[],
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/getFilters', async (_, { getState, rejectWithValue }) => {
    if (getFiltersController?.signal && !getFiltersController?.signal.aborted) getFiltersController.abort();

    const state = getState().assortment;
    const selectedCategory = state.categories[state.currentPathName.toLowerCase()];
    const selectedFilters = getState().shop.shop?.assortmentFilters;
    const assortmentIds = selectedCategory ? [selectedCategory.code] : selectSearchParams(getState()).requestBody;

    getFiltersController = new AbortController();

    const response = await services.assortment.getFilters(
        {
            assortmentIds,
            selectedFilters,
        },
        getFiltersController.signal,
    );

    return response;
});

export const toggleFilter = createAsyncThunk<
    void,
    FilterToggleAction,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/toggleFilter', async (filter, { dispatch }) => {
    dispatch(assortmentSlice.actions.filterToggled(filter));
    dispatch(loadProducts({ page: 1 }));
});

export const clearFilters = createAsyncThunk<
    void,
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/clearFilters', async (_, { dispatch }) => {
    dispatch(assortmentSlice.actions.filtersCleared());
    dispatch(loadProducts({ page: 1 }));
});

export type CategoryUrlMap = {
    name?: string | null;
    description?: string | null;
    code: string;
    children: { name: string; path: string }[];
    parentCode: string;
    parentSlug: string;
    slug: string;
};

export type CategoryUrlMaps = { [key: string]: CategoryUrlMap };

export const getAllCategories = createAsyncThunk<
    { categories: CategoryUrlMaps; categoriesRaw: CatalogNodeModel[] },
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>('assortment/getAllCategories', async (_, { getState, dispatch }) => {
    const response = await services.assortment.getCategories({});
    const rootCode = response?.[0].parent ?? 'root';
    const flatCatalogStructure = flattenCategories([{ children: response, code: rootCode }]).flatCategories;
    return { categories: flatCatalogStructure, categoriesRaw: response };
});

function flattenCategories(
    categories: CatalogNodeModel[],
    parentSlug = '',
    parentCategories = {},
): { flatCategories: CategoryUrlMaps; children: { name: string; path: string }[] } {
    return categories.reduce(
        (acc: any, category: any) => {
            if (!category.code) return acc;
            const key = `${parentSlug}${
                category?.assortmentSlug ? `/${category?.assortmentSlug || ''}`.toLowerCase() : ''
            }`;

            const { flatCategories, children } = flattenCategories(category.children ?? [], key);
            const returnAcc = {
                flatCategories: {
                    ...acc.flatCategories,
                    [key]: {
                        parentSlug: parentSlug,
                        parentCode: category.parent ?? 'root',
                        name: category.name || '',
                        description: category.description,
                        code: category.code,
                        children: children,
                        slug: category.assortmentSlug || '',
                    },
                },
                children: [...acc.children, { name: category.name, path: key }],
            };

            if (category?.children?.length > 0) {
                return {
                    children: [...returnAcc.children],
                    flatCategories: { ...flatCategories, ...returnAcc.flatCategories },
                };
            }

            return returnAcc;
        },
        { flatCategories: parentCategories, children: [] },
    );
}

function flattenFeatured(nodes: CatalogNodeModel[]): CatalogNodeModel[] {
    return nodes.flatMap((node) => {
        const children = node.children ? flattenFeatured(node.children) : [];
        return node.featured ? [node, ...children] : children;
    });
}

// ## UTILITIES
function getDescendantCategoryCodes(category?: CategoryUrlMap, categories?: CategoryUrlMaps): string[] {
    if (!category || !categories) return [];

    const nestedCategories = category?.children.map((child) => categories[child.path]);

    const nestedCategoryCodes =
        nestedCategories.flatMap((nestedCategory) => getDescendantCategoryCodes(nestedCategory, categories)) ?? [];

    if (category.code === 'root') return nestedCategoryCodes;

    return [category.code, ...nestedCategoryCodes];
}

// ## SELECTORS
const selectAssortmentType = (state: RootState) => state.shop.shop?.assortmentType;
export const selectCategoriesRaw = (state: RootState) => state.assortment.categoriesRaw;
export const selectCategories = (state: RootState) => state.assortment.categories;
const selectCurrentPathname = (state: RootState) => state.assortment.currentPathName;
const selectActiveFilters = (state: RootState) => state.assortment.filtersActive;
const selectFilters = (state: RootState) => state.assortment.filters;
const selectSearchPhrase = (state: RootState) => state.assortment.searchPhrase;
const selectAssortmentPageInfo = (state: RootState) => state.assortment.assortmentPageInfo;
export const selectSelectedOrderBy = (state: RootState) => state.assortment.selectedOrderBy;
export const selectFeaturedCategories = createSelector(selectCategoriesRaw, flattenFeatured);

export const selectCompleteActiveFilters = createSelector(
    selectFilters,
    selectActiveFilters,
    (filters, activeFilters) => {
        return activeFilters.map(({ type, values }) => {
            const types = filters?.flatMap((filter) => filter.filter).filter((filter) => filter?.fieldName === type);
            return {
                type: type,
                values: values.map((val) => {
                    const type = types.find((t) => t?.key === val.key);
                    return {
                        ...val,
                        value: type?.name ?? '',
                    };
                }),
            };
        });
    },
);

export const selectSearchParams = createSelector(
    selectAssortmentType,
    selectCurrentPathname,
    selectCategories,
    selectSearchPhrase,
    selectAssortmentPageInfo,
    selectActiveFilters,
    (
        assortmentType,
        currentPathName,
        categories,
        searchPhrase,
        assortmentPageInfo,
        activeFilters,
    ): AssortmentGetProductsPOSTRequest => {
        const pathName = currentPathName.toLowerCase();

        const selectedCategory = categories[pathName.toLowerCase()];

        let categoryCodes: string[] | undefined = [];
        if (assortmentType === ShopAssortmentType.Full) {
            if (selectedCategory) categoryCodes = [selectedCategory.code];
            else categoryCodes = undefined;
        } else {
            if (selectedCategory) categoryCodes = getDescendantCategoryCodes(selectedCategory, categories);
            else {
                categoryCodes = getDescendantCategoryCodes(categories[''], categories);
            }
        }

        return {
            selectedFilters: getFilterParam(activeFilters),
            requestBody: categoryCodes,
            page: assortmentPageInfo.currentPage,
            search: searchPhrase || undefined,
        };
    },
);

export const selectFlattenCompleteActiveFilters = createSelector(selectCompleteActiveFilters, (activeFilters) => {
    const flattenFilters = activeFilters.reduce((acc, filter) => {
        return [...acc, ...filter.values.map((f) => ({ type: filter.type, value: f }))];
    }, new Array<FilterToggleAction>());

    return flattenFilters.length === 0 ? null : flattenFilters;
});

export const selectOrderBy = createSelector(
    selectSelectedOrderBy,
    (selectedOrderBy): OrderBy => {
        return selectedOrderBy ?? 'relevance';
    },
);

export const selectAssortmentPage = createSelector(
    selectCategories,
    selectCurrentPathname,
    (categories, pathname): CategoryUrlMap => {
        return categories[pathname.toLowerCase()];
    },
);

type BreadCrumb = Omit<CategoryUrlMap & { slug?: string }, 'children' | 'code' | 'parentCode' | 'parentSlug'>;

export const selectBreadCrumbs = (currentSlug: string) =>
    createSelector(selectCategories, selectCurrentPathname, function getParent(categories, slug): BreadCrumb[] {
        const currentCategory = categories[slug];
        if (
            currentCategory &&
            currentCategory?.parentSlug! &&
            currentCategory?.parentSlug.length > 0 &&
            categories[currentCategory.parentSlug]
        ) {
            return [
                ...getParent(categories, currentCategory.parentSlug),
                ...(currentSlug === slug ? [] : [{ name: currentCategory?.name, slug: slug } as BreadCrumb]),
            ];
        }

        return [...(currentSlug === slug ? [] : [{ name: currentCategory?.name, slug: slug } as BreadCrumb])];
    });

const selectProducts = (state: RootState) => state.assortment.products;

export const selectProductLinks = createSelector(
    selectProducts,
    (products): Record<string, string> => {
        const links: Record<string, string> = {};
        for (const product of products) {
            if (product.productNumber) {
                links[product.productNumber] = `/product/${product.slug}`;
            }
        }

        return links;
    },
);

export const {
    filterToggled,
    showFilterToggled,
    setAssortmentLocation,
    searchPhraseSet,
    searchPhraseClear,
} = assortmentSlice.actions;
export default assortmentSlice.reducer;
